import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { knowledgeQueryKeys } from "./query-keys";
import type {
  CreateKnowledgeDocumentRequest,
  KnowledgeDocumentDetail,
  KnowledgeDocumentListParams,
  KnowledgeDocumentListResponse,
  SyncResult,
  UpdateKnowledgeDocumentRequest,
} from "../types";

import { instance } from "@/lib/axios/instance";
import useToast from "@/lib/hooks/useToast";

const BASE = "/api/v2/captain/knowledge";

async function clearKnowledgeCache(queryClient: QueryClient) {
  await queryClient.refetchQueries({ queryKey: knowledgeQueryKeys.all });
}

export function useKnowledgeDocuments(params: KnowledgeDocumentListParams = {}) {
  return useQuery({
    queryKey: knowledgeQueryKeys.list(params),
    queryFn: async () => {
      const { data } = await instance.get<KnowledgeDocumentListResponse>(BASE, {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 20,
          search: params.search || undefined,
          syncStatus: params.syncStatus || undefined,
          chunkType: params.chunkType || undefined,
          orderBy: params.orderBy || undefined,
          descending: params.descending || undefined,
        },
      });
      return data;
    },
  });
}

export function useKnowledgeFilterCounts() {
  return useQuery({
    queryKey: [...knowledgeQueryKeys.all, "filter-counts"],
    queryFn: async () => {
      const { data } = await instance.get<KnowledgeDocumentListResponse>(BASE, {
        params: { pageSize: 9999 },
      });
      const syncStatus: Record<string, number> = {};
      const chunkType: Record<string, number> = {};
      for (const doc of data.documents) {
        syncStatus[doc.syncStatus] = (syncStatus[doc.syncStatus] ?? 0) + 1;
        if (doc.chunkType) {
          chunkType[doc.chunkType] = (chunkType[doc.chunkType] ?? 0) + 1;
        }
      }
      return { syncStatus, chunkType, total: data.total };
    },
    staleTime: 60_000,
  });
}

export function useKnowledgeDocument(fileName: string | null) {
  return useQuery({
    queryKey: knowledgeQueryKeys.detail(fileName ?? ""),
    queryFn: async () => {
      const { data } = await instance.get<KnowledgeDocumentDetail>(
        `${BASE}/${fileName}`,
      );
      return data;
    },
    enabled: !!fileName,
  });
}

export function useUploadDocument() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (request: CreateKnowledgeDocumentRequest) =>
      instance.post(BASE, request),
    onSuccess: () => clearKnowledgeCache(queryClient),
    onError: (err) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail;
      showToast(detail ?? "Failed to upload document", "error");
    },
  });
}

export function useUpdateDocument() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: ({
      fileName,
      ...request
    }: UpdateKnowledgeDocumentRequest & { fileName: string }) =>
      instance.put(`${BASE}/${fileName}`, request),
    onSuccess: () => clearKnowledgeCache(queryClient),
    onError: (err) => {
      showToast(
        err instanceof Error ? err.message : "Failed to update document",
        "error",
      );
    },
  });
}

export function useDeleteDocument() {
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (fileName: string) => instance.delete(`${BASE}/${fileName}`),
    onError: (err) => {
      showToast(
        err instanceof Error ? err.message : "Failed to delete document",
        "error",
      );
    },
  });
}

export function useSyncKnowledge(tenantId?: string | null) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async () => {
      const { data } = await instance.post<SyncResult>(`${BASE}/sync`, null, {
        params: tenantId ? { tenantId } : undefined,
      });
      return data;
    },
    onSuccess: async (result) => {
      await new Promise((r) => setTimeout(r, 1500));
      clearKnowledgeCache(queryClient);
      if (!result) return;
      const parts = [
        result.added > 0 && `Added: ${result.added}`,
        result.updated > 0 && `Updated: ${result.updated}`,
        result.deleted > 0 && `Deleted: ${result.deleted}`,
        `Unchanged: ${result.unchanged}`,
      ].filter(Boolean);
      showToast(parts.join(", "), "success", undefined, "Sync complete");
    },
    onError: (err) => {
      showToast(
        err instanceof Error ? err.message : "Sync failed",
        "error",
      );
    },
  });
}
